/**
 * AppShell.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared layout for user-facing pages: theme system, Sidebar, Topbar,
 * Language switcher, Profile menu. Import from here instead of copy-pasting
 * this boilerplate into every page (support.jsx, docs.jsx, community.jsx,
 * tutorials.jsx, live-chat.jsx, status.jsx all use this).
 *
 * Usage in a page:
 *   import { BASE_CSS, Sidebar, Topbar, DEFAULT_SETTINGS, buildThemeVars, applyThemeVars } from "../shared/AppShell";
 *   <style dangerouslySetInnerHTML={{ __html: BASE_CSS + PAGE_CSS }} />
 *   <div className="in-shell"><Sidebar active="/docs" /><div className="in-right">...
 */
import { useState, useEffect, useRef } from "react";
import {
  Bell, Settings, Search, LogOut, UserCircle,
  LayoutDashboard, CandlestickChart, Lightbulb, Users, Globe, HelpCircle,
} from "lucide-react";
import logo from "../icon/logo.png";
import supabase from "../supabase";

/* ─── Theme (mirrors user_settings defaults) ──────────────────────────── */
export const DEFAULT_SETTINGS = {
  theme: "dark",
  accent_color: "#c8f560",
  layout_density: "comfortable",
};

export function resolveTheme(theme) {
  if (theme === "system")
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  return theme;
}

export function buildThemeVars(settings) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  const theme = resolveTheme(s.theme);
  const accent = s.accent_color;

  const hex2rgb = (h) => {
    const c = h.replace("#", "");
    return [parseInt(c.slice(0, 2), 16), parseInt(c.slice(2, 4), 16), parseInt(c.slice(4, 6), 16)];
  };
  const [ar, ag, ab] = hex2rgb(accent);

  const densityMap = {
    compact: { sbW: "220px", mainPad: "16px 20px 32px", gap: "16px" },
    comfortable: { sbW: "256px", mainPad: "24px 28px 40px", gap: "24px" },
    spacious: { sbW: "280px", mainPad: "32px 36px 56px", gap: "32px" },
  };
  const d = densityMap[s.layout_density] || densityMap.comfortable;

  const dark = {
    "--bg": "#080b10", "--surface": "#0e1219", "--surface2": "#141922",
    "--border": "#1e2535", "--border2": "#2a3347",
    "--text": "#e2e8f0", "--muted": "#64748b", "--faint": "#374151", "--input-bg": "#141922",
  };
  const light = {
    "--bg": "#f0f4f8", "--surface": "#ffffff", "--surface2": "#f8fafc",
    "--border": "#e2e8f0", "--border2": "#cbd5e1",
    "--text": "#0f172a", "--muted": "#64748b", "--faint": "#94a3b8", "--input-bg": "#f1f5f9",
  };

  return {
    ...(theme === "light" ? light : dark),
    "--accent": accent,
    "--accent-dim": `rgba(${ar},${ag},${ab},.12)`,
    "--accent-glow": `rgba(${ar},${ag},${ab},.06)`,
    "--accent-rgb": `${ar},${ag},${ab}`,
    "--green": "#34d399", "--green-dim": "rgba(52,211,153,.12)",
    "--red": "#f87171", "--red-dim": "rgba(248,113,113,.12)",
    "--blue": "#60a5fa", "--blue-dim": "rgba(96,165,250,.12)",
    "--purple": "#a78bfa", "--purple-dim": "rgba(167,139,250,.12)",
    "--amber": "#f59e0b", "--amber-dim": "rgba(245,158,11,.12)",
    "--sidebar-w": d.sbW,
    "--topbar-h": "60px",
    "--density-pad": d.mainPad,
    "--layout-gap": d.gap,
    "--sans": "'Space Grotesk',sans-serif",
    "--serif": "'Instrument Serif',serif",
    "--mono": "'JetBrains Mono',monospace",
    "--r-sm": "8px", "--r-md": "12px", "--r-lg": "16px",
  };
}

export function applyThemeVars(vars) {
  Object.entries(vars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));
}

/* ─── Shared CSS: shell, sidebar, topbar, buttons, badges, menus ──────── */
export const BASE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { font-size: 14px; }

  :root {
    --bg:#080b10; --surface:#0e1219; --surface2:#141922;
    --border:#1e2535; --border2:#2a3347;
    --text:#e2e8f0; --muted:#64748b; --faint:#374151;
    --accent:#c8f560; --accent-dim:rgba(200,245,96,.12); --accent-glow:rgba(200,245,96,.06);
    --accent-rgb:200,245,96;
    --green:#34d399; --green-dim:rgba(52,211,153,.12);
    --red:#f87171; --red-dim:rgba(248,113,113,.12);
    --blue:#60a5fa; --blue-dim:rgba(96,165,250,.12);
    --purple:#a78bfa; --purple-dim:rgba(167,139,250,.12);
    --amber:#f59e0b; --amber-dim:rgba(245,158,11,.12);
    --sidebar-w:256px; --topbar-h:60px;
    --density-pad:24px 28px 40px;
    --layout-gap:24px;
    --sans:'Space Grotesk',sans-serif;
    --serif:'Instrument Serif',serif;
    --mono:'JetBrains Mono',monospace;
    --r-sm:8px; --r-md:12px; --r-lg:16px;
  }

  html { -webkit-text-size-adjust:100%; }
  body, #root {
    font-family:var(--sans); background:var(--bg); color:var(--text);
    height:100vh; overflow:hidden; -webkit-font-smoothing:antialiased;
    width:100%; max-width:100vw;
  }
  img, svg { max-width:100%; }
  a { color:inherit; text-decoration:none; }
  button { font-family:var(--sans); cursor:pointer; border:none; background:none; }
  input, select, textarea { font-family:var(--sans); }
  ::-webkit-scrollbar { width:3px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:var(--border2); border-radius:4px; }

  .in-shell { display:grid; grid-template-columns:var(--sidebar-w) 1fr; height:100vh; overflow:hidden; }

  .in-sidebar {
    background:var(--surface); border-right:1px solid var(--border);
    display:flex; flex-direction:column; height:100vh;
    overflow-y:auto; overflow-x:hidden; position:relative; z-index:100;
    flex-shrink:0; transition:transform .25s cubic-bezier(.4,0,.2,1);
  }
  .in-sidebar.open { transform:translateX(0) !important; box-shadow:4px 0 32px rgba(0,0,0,.6) !important; }
  .in-sidebar::after {
    content:''; position:absolute; top:0; right:0; width:1px; height:100%;
    background:linear-gradient(180deg,transparent 0%,var(--accent) 30%,var(--border) 60%,transparent 100%);
    opacity:.15; pointer-events:none;
  }
  .in-brand { display:flex; align-items:center; gap:10px; padding:20px 20px 16px; border-bottom:1px solid var(--border); flex-shrink:0; }
  .in-sb-scroll { flex:1; overflow-y:auto; padding:8px 0; }
  .in-sb-section { padding:10px 20px 4px; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; color:var(--faint); }
  .in-sb-link {
    display:flex; align-items:center; gap:11px; padding:9px 20px;
    font-size:13px; font-weight:500; color:var(--muted);
    border-left:2px solid transparent; transition:all .15s; cursor:pointer;
  }
  .in-sb-link svg { flex-shrink:0; }
  .in-sb-link:hover { color:var(--text); background:var(--accent-glow); border-left-color:var(--border2); }
  .in-sb-link.active { color:var(--accent); background:var(--accent-dim); border-left-color:var(--accent); }
  .in-sb-badge { margin-left:auto; font-size:9px; font-weight:700; background:var(--accent); color:#000; padding:2px 6px; border-radius:5px; }
  .in-sb-logout { color:var(--red); }
  .in-sb-logout:hover { color:var(--red); background:var(--red-dim); border-left-color:var(--red); }
  .in-sb-user { flex-shrink:0; border-top:1px solid var(--border); padding:14px 16px; display:flex; align-items:center; gap:10px; }
  .in-sb-avatar {
    width:36px; height:36px; border-radius:50%;
    background:linear-gradient(135deg,var(--accent) 0%,#78d000 100%);
    color:#000; font-size:12px; font-weight:700;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    box-shadow:0 0 12px rgba(var(--accent-rgb),.3);
  }
  .in-sb-user-name { font-size:13px; font-weight:700; }
  .in-sb-user-role { font-size:10px; color:var(--accent); margin-top:1px; }

  .in-right { grid-column:2; display:flex; flex-direction:column; height:100vh; overflow:hidden; }

  .in-topbar {
    height:var(--topbar-h); flex-shrink:0; display:flex; align-items:center;
    padding:0 28px; background:var(--surface); border-bottom:1px solid var(--border); gap:16px; z-index:50;
  }
  .in-topbar-title { font-family:var(--serif); font-size:20px; color:var(--text); flex:1; }
  .in-topbar-title span { color:var(--accent); font-style:italic; }
  .in-tb-icon {
    width:36px; height:36px; border-radius:var(--r-sm); background:var(--surface2);
    border:1px solid var(--border); display:flex; align-items:center; justify-content:center;
    cursor:pointer; transition:all .15s; color:var(--muted); font-size:18px; position:relative;
  }
  .in-tb-icon:hover { border-color:var(--border2); color:var(--text); }
  .in-notif-dot { position:absolute; top:6px; right:6px; width:6px; height:6px; background:var(--red); border-radius:50%; border:1.5px solid var(--surface); }
  .in-tb-avatar {
    width:36px; height:36px; border-radius:50%; padding:0; margin:0;
    background:linear-gradient(135deg,var(--accent) 0%,#78d000 100%);
    color:#000; font-size:12px; font-weight:700;
    display:flex; align-items:center; justify-content:center; cursor:pointer;
    box-shadow:0 0 10px rgba(var(--accent-rgb),.25); flex-shrink:0;
  }
  .in-hamburger { display:none; flex-direction:column; gap:5px; cursor:pointer; padding:4px; }
  .in-hamburger span { display:block; width:20px; height:2px; background:var(--text); border-radius:2px; }

  .in-main { flex:1; overflow-y:auto; overflow-x:hidden; padding:var(--density-pad); width:100%; }
  @media (min-width:1600px) { .in-main { max-width:1600px; margin:0 auto; } }

  .in-btn {
    display:inline-flex; align-items:center; justify-content:center; gap:7px;
    border-radius:var(--r-sm); font-family:var(--sans); font-weight:600;
    cursor:pointer; transition:all .15s; border:none; white-space:nowrap;
  }
  .in-btn-sm  { font-size:12px; padding:7px 14px; }
  .in-btn-md  { font-size:13px; padding:9px 18px; }
  .in-btn-lg  { font-size:14px; padding:11px 22px; }
  .in-btn-accent { background:var(--accent); color:#000; }
  .in-btn-accent:hover { opacity:.88; box-shadow:0 0 20px rgba(var(--accent-rgb),.3); }
  .in-btn-ghost { background:var(--surface2); border:1px solid var(--border); color:var(--text); }
  .in-btn-ghost:hover { border-color:var(--border2); }

  .in-badge { font-size:10px; font-weight:700; padding:2px 8px; border-radius:4px; white-space:nowrap; }
  .in-badge-green  { background:var(--green-dim);  color:var(--green); }
  .in-badge-blue   { background:var(--blue-dim);   color:var(--blue); }
  .in-badge-amber  { background:var(--amber-dim);  color:var(--amber); }
  .in-badge-red    { background:var(--red-dim);    color:var(--red); }
  .in-badge-muted  { background:rgba(100,116,139,.12); color:var(--muted); }

  /* Generic card used across help-center pages */
  .pg-card { background:var(--surface); border:1px solid var(--border); border-radius:var(--r-lg); overflow:hidden; }
  .pg-card-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid var(--border); gap:12px; flex-wrap:wrap; }
  .pg-card-title { font-size:13px; font-weight:700; display:flex; align-items:center; gap:8px; }
  .pg-card-title svg { color:var(--accent); }

  /* Generic page hero (mirrors sp-hero from support.jsx) */
  .pg-hero {
    position:relative; border-radius:var(--r-lg); overflow:hidden;
    background:linear-gradient(135deg,#0d1520 0%,#0b1628 50%,#0d1018 100%);
    border:1px solid var(--border); padding:36px 36px; margin-bottom:28px;
  }
  .pg-hero-grid {
    position:absolute; inset:0; pointer-events:none;
    background-image:
      linear-gradient(rgba(var(--accent-rgb),.04) 1px,transparent 1px),
      linear-gradient(90deg,rgba(var(--accent-rgb),.04) 1px,transparent 1px);
    background-size:40px 40px;
  }
  .pg-hero-content { position:relative; z-index:2; max-width:620px; }
  .pg-hero-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:2px; color:var(--accent); margin-bottom:10px; display:flex; align-items:center; gap:8px; }
  .pg-hero-label::before { content:''; display:block; width:20px; height:1px; background:var(--accent); }
  .pg-hero-title { font-family:var(--serif); font-size:30px; line-height:1.2; margin-bottom:8px; }
  .pg-hero-title em { color:var(--accent); font-style:italic; }
  .pg-hero-sub { font-size:13px; color:var(--muted); line-height:1.6; }
  .pg-back { display:inline-flex; align-items:center; gap:6px; font-size:12px; color:var(--muted); margin-bottom:16px; cursor:pointer; transition:color .15s; }
  .pg-back:hover { color:var(--accent); }

  /* ── Language switcher ── */
  .lang-switcher { position:relative; display:inline-flex; align-items:center; }
  .lang-btn {
    display:inline-flex; align-items:center; justify-content:center;
    background:var(--surface2); border:1px solid var(--border);
    border-radius:var(--r-sm); width:36px; height:36px;
    cursor:pointer; font-family:var(--sans); color:var(--muted);
    transition:border-color .15s, color .15s;
  }
  .lang-btn:hover { border-color:var(--border2); color:var(--text); }
  .lang-dropdown {
    position:absolute; top:calc(100% + 6px); right:0;
    background:var(--surface); border:1px solid var(--border2);
    border-radius:var(--r-md); padding:6px;
    min-width:176px; z-index:400;
    box-shadow:0 16px 40px rgba(0,0,0,.75);
    animation:pg-fade .18s ease both;
  }
  @keyframes pg-fade { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  .lang-option {
    display:flex; align-items:center; gap:9px;
    padding:8px 10px; border-radius:var(--r-sm);
    font-size:12px; color:var(--muted);
    cursor:pointer; font-family:var(--sans);
    transition:background .12s, color .12s;
  }
  .lang-option:hover { background:var(--surface2); color:var(--text); }
  .lang-option.active { color:var(--accent); background:var(--accent-dim); font-weight:600; }
  .lang-section-label { font-size:9px; font-weight:700; color:var(--faint); letter-spacing:.1em; text-transform:uppercase; padding:4px 10px 6px; }

  /* ── Profile menu ── */
  .profile-menu { position:relative; display:inline-flex; align-items:center; flex-shrink:0; }
  .profile-menu .in-tb-avatar { border:none; }
  .profile-dropdown {
    position:absolute; top:calc(100% + 8px); right:0;
    background:var(--surface); border:1px solid var(--border2);
    border-radius:var(--r-md); padding:6px;
    min-width:180px; z-index:400;
    box-shadow:0 16px 40px rgba(0,0,0,.75);
    animation:pg-fade .18s ease both;
  }
  .profile-dd-item {
    display:flex; align-items:center; gap:9px;
    padding:9px 10px; border-radius:var(--r-sm);
    font-size:12px; font-weight:500; color:var(--text);
    cursor:pointer; font-family:var(--sans);
    transition:background .12s, color .12s;
  }
  .profile-dd-item svg { flex-shrink:0; }
  .profile-dd-item:hover { background:var(--surface2); }
  .profile-dd-divider { height:1px; background:var(--border); margin:6px 2px; }
  .profile-dd-logout { color:var(--red); }
  .profile-dd-logout:hover { background:var(--red-dim); color:var(--red); }

  .goog-te-banner-frame, #goog-gt-tt, .goog-te-balloon-frame { display:none !important; }
  body { top:0 !important; }
  .skiptranslate { display:none !important; }

  @media (max-width:768px) {
    .in-shell { grid-template-columns:1fr !important; }
    .in-sidebar {
      position:fixed !important; top:0 !important; left:0 !important;
      transform:translateX(-100%) !important; z-index:300 !important;
      width:min(280px, 84vw) !important;
    }
    .in-sidebar.open { transform:translateX(0) !important; box-shadow:4px 0 32px rgba(0,0,0,.6) !important; }
    .in-right { grid-column:1; }
    .in-hamburger { display:flex; }
    .pg-hero { padding:26px 20px; }
    .pg-hero-title { font-size:24px; }
  }
  @media (max-width:600px) {
    .in-main { padding:16px; }
    .in-topbar { padding:0 12px; gap:8px; }
  }
  @media (max-width:460px) {
    .in-topbar-title { display:none; }
    .lang-btn, .in-tb-icon, .in-tb-avatar { width:32px; height:32px; }
  }
`;

/* ─── Nav config ───────────────────────────────────────────────────────── */
const NAV = [
  { section: "Markets" },
  { Icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
  { Icon: CandlestickChart, label: "Trading", href: "/terminal" },
  { Icon: Lightbulb, label: "Insights", href: "/insights" },
  { section: "Social" },
  { Icon: Users, label: "Copy Trading", badge: "3", href: "/copy-trading" },
  { Icon: UserCircle, label: "Profile", href: "/profile" },
  { Icon: Globe, label: "Marketplace", href: "/market-place" },
  { section: "Account" },
  { Icon: Settings, label: "Settings", href: "/settings" },
  { Icon: HelpCircle, label: "Support", href: "/support" },
  { Icon: LogOut, label: "Log Out", logout: true },
];

/* ─── Sidebar ──────────────────────────────────────────────────────────── */
export function Sidebar({ open, active = "/support" }) {
  const [portfolio, setPortfolio] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [profile, setProfile] = useState(null);

  async function handleLogout() {
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  useEffect(() => {
    async function fetchSidebarData() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [snapRes, walletRes, profileRes] = await Promise.all([
        supabase.from("portfolio_snapshots").select("total_value, daily_pnl, total_pnl_pct")
          .eq("user_id", user.id).order("snapped_at", { ascending: false }).limit(1).maybeSingle(),
        supabase.from("wallets").select("balance, currency").eq("user_id", user.id).order("balance", { ascending: false }),
        supabase.from("users").select("first_name, last_name, plan, currency").eq("id", user.id).single(),
      ]);

      setPortfolio(snapRes.data ?? { total_value: 0, daily_pnl: 0, total_pnl_pct: 0 });

      if (profileRes.data?.currency && walletRes.data?.length) {
        const preferred = walletRes.data.find((w) => w.currency === profileRes.data.currency);
        setWallet(preferred ?? walletRes.data[0]);
      } else if (walletRes.data?.length) {
        setWallet(walletRes.data[0]);
      } else {
        setWallet({ balance: 0, currency: profileRes.data?.currency ?? "USD" });
      }

      if (profileRes.data) setProfile(profileRes.data);
    }
    fetchSidebarData();
  }, []);

  const initials = profile ? `${profile.first_name[0]}${profile.last_name[0]}` : "—";
  const fullName = profile ? `${profile.first_name} ${profile.last_name}` : "—";
  const planLabel = profile ? profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1) + " Member" : "—";

  return (
    <aside className={`in-sidebar${open ? " open" : ""}`}>
      <div className="in-brand">
        <img src={logo} alt="vantrexmarket" style={{ height: "52px", width: "auto" }} />
      </div>
      <div className="in-sb-scroll">
        {NAV.map((n, i) =>
          n.section ? (
            <div key={i} className="in-sb-section">{n.section}</div>
          ) : n.logout ? (
            <div key={i} className="in-sb-link in-sb-logout" onClick={handleLogout} role="button" tabIndex={0}>
              <n.Icon size={18} />{n.label}
            </div>
          ) : (
            <a key={i} className={`in-sb-link${active === n.href ? " active" : ""}`} href={n.href}>
              <n.Icon size={18} />{n.label}
              {n.badge && <span className="in-sb-badge">{n.badge}</span>}
            </a>
          )
        )}
      </div>
      <div className="in-sb-user">
        <div className="in-sb-avatar">{initials}</div>
        <div>
          <div className="in-sb-user-name">{fullName}</div>
          <div className="in-sb-user-role">{planLabel}</div>
        </div>
      </div>
    </aside>
  );
}

/* ─── Language switcher ───────────────────────────────────────────────── */
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh-CN", label: "中文", flag: "🇨🇳" },
  { code: "ja", label: "日本語", flag: "🇯🇵" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
  { code: "yo", label: "Yoruba", flag: "🇳🇬" },
  { code: "ha", label: "Hausa", flag: "🇳🇬" },
];

const initGoogleTranslate = () => {
  if (window._gtInitDone) return;
  window._gtInitDone = true;
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "display:none!important;position:absolute;left:-9999px;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement({ pageLanguage: "en", autoDisplay: false }, "google_translate_element");
    const saved = localStorage.getItem("tf_lang");
    if (saved && saved !== "en") setTimeout(() => applyGoogleTranslate(saved), 800);
  };
  const script = document.createElement("script");
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.head.appendChild(script);
};

const applyGoogleTranslate = (langCode) => {
  if (langCode === "en") {
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    document.cookie = "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=" + window.location.hostname;
    window.location.reload();
    return;
  }
  const pair = "/en/" + langCode;
  document.cookie = "googtrans=" + pair + "; path=/";
  document.cookie = "googtrans=" + pair + "; path=/; domain=" + window.location.hostname;
  const sel = document.querySelector(".goog-te-combo");
  if (sel) { sel.value = langCode; sel.dispatchEvent(new Event("change")); }
  else window.location.reload();
};

export function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem("tf_lang") || "en");
  const ref = useRef(null);

  useEffect(() => { initGoogleTranslate(); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const select = (code) => {
    setCurrent(code);
    localStorage.setItem("tf_lang", code);
    setOpen(false);
    applyGoogleTranslate(code);
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];

  return (
    <div className="lang-switcher" ref={ref}>
      <button className="lang-btn" onClick={() => setOpen((o) => !o)} title="Change language">
        <span style={{ fontSize: "15px" }}>{active.flag}</span>
      </button>
      {open && (
        <div className="lang-dropdown">
          <div className="lang-section-label">Language</div>
          {LANGUAGES.map((l) => (
            <div key={l.code} className={"lang-option" + (l.code === current ? " active" : "")} onClick={() => select(l.code)}>
              <span style={{ fontSize: "15px" }}>{l.flag}</span>
              <span>{l.label}</span>
              {l.code === current && <span style={{ marginLeft: "auto", fontSize: "10px" }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Profile menu ─────────────────────────────────────────────────────── */
export function ProfileMenu({ initials }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  return (
    <div className="profile-menu" ref={ref}>
      <button className="in-tb-avatar" onClick={() => setOpen((o) => !o)} aria-haspopup="true" aria-expanded={open}>
        {initials}
      </button>
      {open && (
        <div className="profile-dropdown">
          <a href="/profile" className="profile-dd-item" onClick={() => setOpen(false)}>
            <UserCircle size={15} /> Profile
          </a>
          <div className="profile-dd-divider" />
          <div className="profile-dd-item profile-dd-logout" onClick={handleLogout}>
            <LogOut size={15} /> Logout
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Topbar ───────────────────────────────────────────────────────────── */
export function Topbar({ onMenu, user, title, accent }) {
  const initials = user ? `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() : "—";
  return (
    <header className="in-topbar">
      <div className="in-hamburger" onClick={onMenu}><span /><span /><span /></div>
      <div className="in-topbar-title">{title} <span>{accent}</span></div>
      <a href="/search" className="in-tb-icon"><Search size={18} /></a>
      <LanguageSwitcher />
      <div className="in-tb-icon"><a href="/notification"><Bell size={18} /><span className="in-notif-dot" /></a></div>
      <div className="in-tb-icon"><a href="/settings"><Settings size={18} /></a></div>
      <ProfileMenu initials={initials} />
    </header>
  );
}

/* ─── Shared: fetch current user for Topbar ───────────────────────────── */
export function useCurrentUser() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user: authUser } }) => {
      if (!authUser) return;
      const { data } = await supabase.from("users").select("first_name, last_name").eq("id", authUser.id).maybeSingle();
      if (data) setUser(data);
    });
  }, []);
  return user;
}